// apps/office/src/pages/PartnerClientPayments.jsx
import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Search, Receipt, Loader2 } from 'lucide-react';
import pb from '../../lib/pocketbaseClient';
import { formatCurrency } from '../../lib/utils';

export default function PartnerClientPayments() {
  const { user } = useAuth();
  const [payments, setPayments] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const partners = await pb.collection('partners').getFullList({
          filter: `createdBy = "${user.id}" || email = "${user.email}"`,
        });
        if (!partners.length) {
          setLoading(false);
          return;
        }
        const partner = partners[0];

        const assignments = await pb.collection('partner_site_assignments').getFullList({
          filter: `partner = "${partner.id}" && isActive = true`,
          expand: 'site',
        });
        const siteIds = assignments.map(a => a.expand?.site?.id).filter(Boolean);

        if (!siteIds.length) {
          setLoading(false);
          return;
        }

        const records = await pb.collection('client_payments').getFullList({
          filter: `project ~ "${siteIds.join('||')}"`,
          sort: '-paymentDate',
          expand: 'project',
        });

        setPayments(records);
        setFiltered(records);
      } catch (error) {
        console.error('Error fetching client payments:', error);
      } finally {
        setLoading(false);
      }
    };

    if (user) fetchData();
  }, [user]);

  useEffect(() => {
    const f = payments.filter(p =>
      p.expand?.project?.name?.toLowerCase().includes(search.toLowerCase()) ||
      p.note?.toLowerCase().includes(search.toLowerCase())
    );
    setFiltered(f);
  }, [search, payments]);

  const total = filtered.reduce((sum, p) => sum + (p.amount || 0), 0);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
            <Receipt className="w-6 h-6 text-amber-500" />
            Client Payment Received
          </h1>
          <p className="text-slate-500 text-sm">View client payments for your assigned sites (read-only)</p>
        </div>
        <div className="text-xs text-slate-400 bg-amber-50 px-3 py-1 rounded-full border border-amber-200">
          ?? Read-only
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-4">
            <p className="text-xs text-slate-500 uppercase font-semibold">Total Received</p>
            <p className="text-2xl font-bold text-green-600">{formatCurrency(total)}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-xs text-slate-500 uppercase font-semibold">Records</p>
            <p className="text-2xl font-bold text-slate-800">{filtered.length}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-xs text-slate-500 uppercase font-semibold">Sites</p>
            <p className="text-2xl font-bold text-blue-600">
              {new Set(payments.map(p => p.project)).size}
            </p>
          </CardContent>
        </Card>
      </div>

      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Search by project or note..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>
      </div>

      {filtered.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-xl border border-slate-200">
          <Receipt className="w-12 h-12 mx-auto text-slate-300 mb-3" />
          <h3 className="text-lg font-semibold text-slate-700">No Payments Found</h3>
          <p className="text-sm text-slate-400">No client payments recorded for your assigned sites.</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <table className="w-full text-left text-sm">
            <thead className="bg-slate-50 border-b">
              <tr>
                <th className="px-4 py-3 text-xs font-semibold text-slate-600 uppercase">Project</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-slate-600 uppercase">Amount</th>
                <th className="px-4 py-3 text-xs font-semibold text-slate-600 uppercase">Date</th>
                <th className="px-4 py-3 text-xs font-semibold text-slate-600 uppercase">Method</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {filtered.map(p => (
                <tr key={p.id} className="hover:bg-slate-50 transition">
                  <td className="px-4 py-3 font-medium">{p.expand?.project?.name || 'N/A'}</td>
                  <td className="px-4 py-3 text-right font-semibold text-green-600">
                    {formatCurrency(p.amount)}
                  </td>
                  <td className="px-4 py-3">{p.paymentDate || 'N/A'}</td>
                  <td className="px-4 py-3 capitalize">{p.method || 'N/A'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}